import axios from './index'
import { Router as router, baseUrl } from 'src/router'
import { store } from 'src/store'
import { Dialog as QDialog, Notify } from 'quasar'
import Dialog from 'src/helpers/dialog'
import DebugLogDialog from 'src/axios/dialogs/DebugLogDialog'
import { Loading } from 'components/partials/Loading'
import Bus from 'src/helpers/bus'
import maintenance from './../notifications/global/maintenance'
import pkg from './../../package.json'

let isShowingError = false

/* Handle invalid statuses */
const validateStatusFunc = function (statusCode, statusName = null, message = null, payload = {}) {
  Loading.hide()
  switch (statusCode) {
    /* Maintenance / Out of date UI version */
    case 503: {
      if (statusName === 'maintenance') {
        maintenance(payload)
      }
      else if (statusName === 'refresh') {
        Bus.emit('version:refresh')
      }
      return {
        status: statusName || 'maintenance',
        errors: {}
      }
    }
    /* Mixed results for bulk editing that returns a mix of errors and successes */
    case 207: {
      return {
        status: 'mixed',
        message,
        payload,
        errors: payload.statuses.filter(obj => obj.status === 'error')
      }
    }
    /* Server error */
    case 500: {
      if (!isShowingError) {
        isShowingError = true
        Dialog({
          title: 'Something went wrong',
          message: 'An error occurred while processing this request. Please try reloading the app to resolve this issue.'
        }).onDismiss(() => {
          isShowingError = false
        })
      }
      console.log(`Returned status ${statusCode} - an error occurred. Please reload the page.`)
      return {
        status: 'error',
        message: 'Could not connect to our server. Please try again.',
        errors: {}
      }
    }
    /* Unauthorized */
    /* Forbidden */
    case 401:
    case 403: {
      if (!isShowingError) {
        isShowingError = true
        Dialog({
          title: 'Permission Error',
          message
        }).onDismiss(() => {
          isShowingError = false
        })
      }
      // Only log user out if they are not authorised
      if (statusCode === 401 && store.getters.isLoggedIn) {
        router.push(baseUrl() + 'logout').catch(() => {})
      }
      return {
        status: 'error',
        message: 'Your session has expired.',
        errors: {}
      }
    }
    /* Too many requests */
    case 429: {
      if (!isShowingError) {
        isShowingError = true
        Dialog({
          title: 'Too many requests',
          message: 'You have made too many requests to our server! Please wait a minute and then perform your request again.'
        }).onDismiss(() => {
          isShowingError = false
        })
      }
      return {
        status: 'error',
        message: 'Could not connect to our server. Please try again.',
        errors: {}
      }
    }
    /* Request entity too large */
    case 413: {
      const postSizeLimit = store.getters.getSystemSetting('post_max_size') || 0

      console.log(`Returned status ${statusCode} - Payload was too large, please try submit a smaller request.`)
      return {
        status: 'error',
        message: `You attempted to submit a request which is too large. If you are attempting to upload files, please try submit a smaller batch. Maximum payload size is ${postSizeLimit}MB.`,
        errors: {}
      }
    }
    default: {
      return {
        status: 'error',
        message: 'Could not connect to our server. Please try again.',
        errors: {}
      }
    }
  }
}

/**
 * Attempt to determine if the payload is too large to correctly handle 413 error
 * @param payload
 * @returns {boolean}
 */
function checkPayloadIsTooLarge (payload = '') {
  const maxSize = store.getters.getSystemSetting('post_max_size') || null
  if (!maxSize) {
    return false
  }

  // Tricky to stringify because FormData can contain many different elements including binary (file).
  // Easier to just return true; as its the most likely cause
  if (payload instanceof FormData) {
    return true
  }

  // Stringify the payload and check if it exceeds the post_max_size variable
  const payloadSizeInBytes = JSON.stringify(payload).length
  const maxAllowedSizeInBytes = maxSize * 1024 * 1024 // Example: 10 MB
  return payloadSizeInBytes >= maxAllowedSizeInBytes
}

/* Exports */
async function request (method = 'post', url, params = {}, data = {}, headers = {}, skipAuth = false) {
  const sysHeaders = {}
  const mkey = store.getters.getSetting('mkey')
  if (mkey) {
    sysHeaders['Mkey'] = mkey
  }
  if (!skipAuth && store.getters.isLoggedIn) {
    sysHeaders['Authorization'] = 'Bearer ' + store.getters.getAccessToken
    sysHeaders['App-Version'] = pkg.version
  }

  const options = {
    method,
    url,
    params,
    data,
    headers: sysHeaders,
    validateStatus (status) {
      return status === 200 || status === 422
    },
    ...headers
  }

  return axios(options)
    .then(function (response) {
      if (!response.data) {
        response.data = {}
      }

      const statusCode = response.status
      const url = response.config.url
      const method = response.config.method

      const status = response.data.status
      const errors = !Array.isArray(response.data.errors) ? response.data.errors : {}
      const payload = response.data.payload
      const message = response.data.message

      // Display a generic notification if the endpoint fails with error code/status
      if (statusCode === 422 && status === 'error') {
        Notify.create({
          message,
          color: 'red-8',
          actions: [
            {
              label: 'Debug',
              color: 'yellow',
              padding: 'xs',
              size: '12px',
              handler: () => {
                QDialog.create({
                  component: DebugLogDialog,
                  componentProps: {
                    url,
                    method,
                    message,
                    errors,
                    sentData: data
                  }
                })
              }
            }
          ]
        })
      }

      return {
        status,
        errors,
        payload,
        message
      }
    })
    .catch(function (err) {
      let statusCode = null
      let statusName = null
      let message = null
      let payload = {}
      if (err.response) {
        statusCode = err.response.status
        if (err.response.data) {
          statusName = err.response.data.status
          message = err.response.data.message
          payload = err.response.data.payload
        }
      }
      // 413 usually won't return properly from the backend if its too large.
      // Attempt to check if the submit payload is too large for the backend to handle
      else if (err.code === 'ERR_NETWORK' && checkPayloadIsTooLarge(options.data)) {
        statusCode = 413
      }
      return validateStatusFunc(statusCode, statusName, message, payload)
    })
}

export function post (url, data = {}, headers = {}, skipAuth = false) {
  return request('post', url, {}, data, headers, skipAuth)
}

export function put (url, data = {}, headers = {}, skipAuth = false) {
  return request('put', url, {}, data, headers, skipAuth)
}

export function patch (url, data = {}, headers = {}, skipAuth = false) {
  return request('patch', url, {}, data, headers, skipAuth)
}

// Delete is a reserved keyword
export function remove (url, params = {}, headers = {}, skipAuth = false) {
  return request('delete', url, params, {}, headers, skipAuth)
}

export function get (url, params = {}, headers = {}, skipAuth = false) {
  return request('get', url, params, {}, headers, skipAuth)
}
